import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import MediaUploader from './MediaUploader';
import BasicInfoSection from './create-event/BasicInfoSection';
import LocationSection from './create-event/LocationSection';
import PricingSection from './create-event/PricingSection';
import { PAYMENTS_ENABLED } from '../config/features';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert DD/MM/AAAA + HH:MM (24h) to ISO 8601 with Argentina offset (-03:00).
// Returns null when either input is not a valid Argentine datetime.
const argDateTimeToISO = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const dateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr.trim());
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeStr.trim());
  if (!dateMatch || !timeMatch) return null;
  const [, dd, mm, yyyy] = dateMatch;
  const [, hh, min] = timeMatch;
  const d = parseInt(dd, 10);
  const m = parseInt(mm, 10);
  const y = parseInt(yyyy, 10);
  const h = parseInt(hh, 10);
  const mi = parseInt(min, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  // Confirm real calendar day (rejects 31/02, 31/04, etc.)
  const check = new Date(y, m - 1, d, h, mi);
  if (check.getFullYear() !== y || check.getMonth() !== m - 1 || check.getDate() !== d) return null;
  // Argentina offset -03:00 — preserves the wall-clock time entered by the organizer
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:00-03:00`;
};

const validateDate = (dateStr) => {
  if (!dateStr.trim()) return 'Ingresá la fecha en formato DD/MM/AAAA.';
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return 'Formato inválido. Usá DD/MM/AAAA.';
  const [dd, mm, yyyy] = dateStr.split('/').map(Number);
  if (mm < 1 || mm > 12) return 'Mes inválido (01 a 12).';
  if (dd < 1 || dd > 31) return 'Día inválido (01 a 31).';
  const check = new Date(yyyy, mm - 1, dd);
  if (check.getFullYear() !== yyyy || check.getMonth() !== mm - 1 || check.getDate() !== dd) {
    return 'Fecha inexistente (por ejemplo 31/02).';
  }
  return '';
};

const validateTime = (timeStr) => {
  if (!timeStr.trim()) return 'Ingresá la hora en formato HH:MM (24hs).';
  if (!/^\d{2}:\d{2}$/.test(timeStr)) return 'Formato inválido. Usá HH:MM (24hs).';
  const [hh, mm] = timeStr.split(':').map(Number);
  if (hh > 23 || mm > 59) return 'Hora inválida (00:00 a 23:59).';
  return '';
};

const buildErrors = (formData, priceTiers, media, videoCount) => {
  const e = {};
  if (!formData.title.trim()) e.title = 'El título es obligatorio.';
  if (!formData.description_short.trim()) e.description_short = 'La descripción corta es obligatoria.';
  else if (formData.description_short.length > 150) e.description_short = 'Máximo 150 caracteres.';
  if (!formData.description_long.trim()) e.description_long = 'La descripción completa es obligatoria.';
  if (formData.category === 'Otros' && !formData.custom_category.trim()) {
    e.custom_category = 'Indicá qué categoría (ej: Stand Up, Feria).';
  }
  const dateErr = validateDate(formData.date);
  if (dateErr) e.date = dateErr;
  const timeErr = validateTime(formData.time);
  if (timeErr) e.time = timeErr;

  const cap = parseInt(formData.capacity, 10);
  if (!formData.capacity) e.capacity = 'La capacidad es obligatoria.';
  else if (isNaN(cap) || cap <= 0) e.capacity = 'La capacidad debe ser un número mayor a 0.';

  if (formData.price !== '' && (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) < 0)) {
    e.price = 'El precio debe ser un número mayor o igual a 0.';
  }
  if (formData.presale_price !== '' || formData.presale_deadline !== '') {
    const pp = parseFloat(formData.presale_price);
    if (isNaN(pp) || pp < 0) e.presale_price = 'Precio de preventa inválido.';
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(formData.presale_deadline)) {
      e.presale_deadline = 'Fecha de preventa en formato DD/MM/AAAA.';
    }
  }
  priceTiers.forEach((t, i) => {
    if (!t.name.trim()) e[`tier_${i}_name`] = 'Nombre requerido';
    if (t.price === '' || isNaN(parseFloat(t.price)) || parseFloat(t.price) < 0) {
      e[`tier_${i}_price`] = 'Precio inválido';
    }
  });

  if (media.length === 0) e.media = 'Subí al menos 1 imagen o video.';
  if (videoCount > 1) e.media = 'Sólo se permite 1 video por evento.';

  if (formData.modality !== 'virtual' && !formData.address.trim()) {
    e.address = 'La dirección es obligatoria para eventos presenciales o híbridos.';
  }
  return e;
};

// Input masks: fixed separators, user types digits only
const maskDate = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const maskTime = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}:${d.slice(2)}`;
};

const MASKED_FIELDS = { date: maskDate, presale_deadline: maskDate, time: maskTime };

const CreateEventForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description_short: '',
    description_long: '',
    category: 'Música',
    custom_category: '',
    event_type: 'único',
    modality: 'presencial',
    date: '',      // DD/MM/AAAA
    time: '',      // HH:MM  (24hs)
    price: '',
    presale_price: '',
    presale_deadline: '', // DD/MM/AAAA
    capacity: '',
    is_solidarity: false,
    venue_name: '',
    address: '',
    city: 'Salta',
    tags: '',
  });
  const [priceTiers, setPriceTiers] = useState([]); // [{name, price, description}]
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});

  const videoCount = media.filter((m) => m.kind === 'video').length;

  const handleChange = (field, value) => {
    const masked = MASKED_FIELDS[field] ? MASKED_FIELDS[field](value) : value;
    setFormData((prev) => ({ ...prev, [field]: masked }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const addTier = () => setPriceTiers([...priceTiers, { name: '', price: '', description: '' }]);
  const updateTier = (i, field, value) => {
    const next = [...priceTiers];
    next[i] = { ...next[i], [field]: value };
    setPriceTiers(next);
  };
  const removeTier = (i) => setPriceTiers(priceTiers.filter((_, idx) => idx !== i));

  const errors = useMemo(
    () => buildErrors(formData, priceTiers, media, videoCount),
    [formData, priceTiers, media, videoCount]
  );

  const isValid = Object.keys(errors).length === 0;
  const missingCount = Object.keys(errors).length;

  // Show error under a field if either the user touched it OR they attempted a submit
  const err = (field) => (touched[field] || touched.__submitAttempted) ? errors[field] : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark all required fields as touched so their inline errors show
    setTouched((prev) => ({
      ...prev,
      __submitAttempted: true,
      title: true,
      description_short: true,
      description_long: true,
      custom_category: true,
      date: true,
      time: true,
      capacity: true,
      address: true,
      media: true,
    }));

    if (!isValid) {
      toast.error(`Revisá los campos marcados en rojo (${missingCount} pendiente${missingCount === 1 ? '' : 's'}).`);
      setTimeout(() => {
        const firstErr = document.querySelector('[data-field-error="true"]');
        if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setLoading(true);
    try {
      const images = media.filter((m) => m.kind === 'image').map((m) => m.url);
      const firstVideo = media.find((m) => m.kind === 'video')?.url || null;

      const startIso = argDateTimeToISO(formData.date, formData.time);
      const presaleIso = formData.presale_deadline
        ? argDateTimeToISO(formData.presale_deadline, '23:59')
        : null;

      const payload = {
        title: formData.title.trim(),
        description_short: formData.description_short.trim(),
        description_long: formData.description_long.trim(),
        category: formData.category,
        custom_category: formData.category === 'Otros' ? formData.custom_category.trim() : null,
        event_type: formData.event_type,
        modality: formData.modality,
        start_date: startIso,
        price: formData.price === '' ? 0 : parseFloat(formData.price),
        presale_price: formData.presale_price === '' ? null : parseFloat(formData.presale_price),
        presale_deadline: presaleIso,
        price_tiers: priceTiers.map((t) => ({
          name: t.name.trim(),
          price: parseFloat(t.price),
          description: t.description?.trim() || null,
        })),
        capacity: parseInt(formData.capacity, 10),
        is_solidarity: formData.is_solidarity,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        location: formData.address.trim() ? {
          address: formData.address.trim(),
          venue_name: formData.venue_name.trim() || null,
          lat: -24.7821,   // TODO: geocode
          lng: -65.4232,
          city: formData.city.trim() || 'Salta',
        } : null,
        images,
        video_url: firstVideo,
      };

      await axios.post(`${API}/events`, payload, {
        withCredentials: true,
      });
      toast.success('¡Evento creado como borrador! Ahora podés enviarlo a revisión.');
      onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(' · ')
        : (detail || error.message || 'Error al crear el evento.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = (field) => `bg-white border-[#E5E5E5] text-[#0A0A0A] ${err(field) ? 'border-red-500 focus:border-red-500' : ''}`;

  const renderFieldError = (field) => {
    const message = err(field);
    if (!message) return null;
    return (
      <p data-field-error="true" data-testid={`error-${field}`} className="text-xs text-red-600 mt-1 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {message}
      </p>
    );
  };

  const sectionProps = { formData, handleChange, inputCls, renderFieldError };

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-event-modal">
      <div className="bg-white border border-[#E5E5E5] rounded-lg w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between flex-shrink-0 bg-white">
          <h2 className="text-2xl font-heading font-bold text-[#0A0A0A]">Crear nuevo evento</h2>
          <Button onClick={onClose} variant="ghost" size="sm" className="text-[#737373]" data-testid="close-form">✕</Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4" noValidate data-testid="create-event-form-body">
          <BasicInfoSection {...sectionProps} />

          <LocationSection {...sectionProps} />

          {/* Precios ocultos mientras los pagos están desactivados (eventos se crean gratis) */}
          {PAYMENTS_ENABLED && (
            <PricingSection
              {...sectionProps}
              priceTiers={priceTiers}
              addTier={addTier}
              updateTier={updateTier}
              removeTier={removeTier}
              errors={errors}
              touched={touched}
            />
          )}

          <div className="space-y-2">
            <Label className="text-[#0A0A0A]">Fotos y video (máximo 3) *</Label>
            <MediaUploader value={media} onChange={(v) => { setMedia(v); setTouched((p) => ({ ...p, media: true })); }} max={3} />
            {renderFieldError("media")}
          </div>

          <div>
            <Label className="text-[#0A0A0A]">Tags (separados por coma)</Label>
            <Input value={formData.tags} onChange={(e) => handleChange('tags', e.target.value)} placeholder="música, folklore, cultura" className={inputCls('tags')} />
          </div>

          <div className="flex items-center gap-3 p-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg">
            <Switch checked={formData.is_solidarity} onCheckedChange={(v) => handleChange('is_solidarity', v)} data-testid="solidarity-switch" />
            <Label className="text-[#0A0A0A]">Es un evento solidario</Label>
          </div>

          {/* Sticky bottom bar with validation summary */}
          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t border-[#E5E5E5] flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {!isValid && (
              <div className="flex-1 text-sm text-red-600 flex items-center gap-2" data-testid="validation-summary">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{missingCount} campo{missingCount === 1 ? '' : 's'} para completar</span>
              </div>
            )}
            {isValid && (
              <div className="flex-1 text-sm text-green-600">✓ Listo para crear</div>
            )}
            <div className="flex gap-3">
              <Button type="button" onClick={onClose} variant="outline" className="border-[#E5E5E5]" data-testid="cancel-button">Cancelar</Button>
              <Button
                type="submit"
                aria-disabled={!isValid || loading}
                className={isValid ? 'bg-primary hover:bg-primary/90 text-white' : 'bg-[#E5E5E5] text-[#A3A3A3] hover:bg-[#E5E5E5] cursor-not-allowed'}
                data-testid="submit-event-button"
              >
                {loading ? 'Creando...' : 'Crear evento'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventForm;
